import { useEffect, useState } from 'react';
import './App.css';
import AuthManager, { useAuth } from './Auth/AuthManager'
import EditorWindow from './EditorWindow/EditorWindow';
import ProjectList from './ProjectList/ProjectList';
import LoadingScreen from './components/LoadingScreen';
import AppHeader from './components/AppHeader';
import SignUpDialog from './components/SignUpDialog';
import { ThemeProvider, createTheme } from '@mui/material';
import { ReactFlowProvider } from 'reactflow';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import useStore from './store';
import { socket } from './socket';
import { initAnalytics, trackPageView, trackTemplate, trackUserType } from './analytics';

const darkTheme = createTheme({
  typography: {
    fontFamily: 'Inter'
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#1B072D',
      contrastText: '#fff'
    },
    secondary: {
      main: '#16DCB8',
    },
    action: {
      // Improve disabled icon visibility on dark backgrounds
      disabled: 'rgba(255, 255, 255, 0.4)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  components: {
    // Ensure disabled IconButtons on AppBar are more visible
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.4)',
          },
        },
      },
    },
  },
});

function AppContent() {
  const connected = useStore((state) => state.connected);
  const currentView = useStore((state) => state.currentView);
  const selectedProject = useStore((state) => state.selectedProject);
  const selectProject = useStore((state) => state.selectProject);
  const { isGuest, isAuthenticated } = useAuth();
  const [templateLoadAttempted, setTemplateLoadAttempted] = useState(false);

  // Initialize analytics once
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('connecting', !connected);
  }, [connected]);

  // Track view changes as virtual pageviews
  useEffect(() => {
    if (connected) {
      const pagePath = currentView === 'editor' && selectedProject
        ? `/editor/${selectedProject.name}`
        : `/${currentView}`;

      trackPageView(pagePath, currentView);
    }
  }, [currentView, selectedProject, connected]);

  // Track user type changes
  useEffect(() => {
    if (connected) {
      trackUserType(isGuest, isAuthenticated);
    }
  }, [isGuest, isAuthenticated, connected]);

  // Check URL for template parameter and load it
  useEffect(() => {
    if (!connected || templateLoadAttempted) return;

    const path = window.location.pathname;
    const templateMatch = path.match(/^\/templates\/(.+)$/);

    if (templateMatch) {
      const templateName = decodeURIComponent(templateMatch[1]);
      // Construct the full template path
      const templatePath = `templates/${templateName}.flow`;

      setTemplateLoadAttempted(true);

      // Track template usage
      trackTemplate(templateName);

      const event = { event_type: 'read', project: templatePath };
      socket.emit('project', event);
    }
  }, [connected, templateLoadAttempted]);

  // Listen for project load events and handle errors
  useEffect(() => {
    const onProjectEvent = (data: any) => {
      if (data.event_type === 'read' && data.project) {
        // Successfully loaded - clean up URL
        window.history.replaceState({}, '', '/');
      }
    };

    const onErrorEvent = (data: any) => {
      // Check if this is a template load error
      const path = window.location.pathname;
      if (path.startsWith('/templates/')) {
        enqueueSnackbar(`Template not found: ${path}`, { variant: 'error' });
        // Clean up URL and go back to project list
        window.history.replaceState({}, '', '/');
      }
    };

    socket.on('project', onProjectEvent);
    socket.on('error', onErrorEvent);

    return () => {
      socket.off('project', onProjectEvent);
      socket.off('error', onErrorEvent);
    };
  }, []);

  if (!connected) {
    return <LoadingScreen message="Connecting ..." />;
  }

  const renderView = () => {
    if (currentView === 'editor' && selectedProject) {
      return <EditorWindow project={selectedProject} />;
    }
    return (
      <ProjectList
        onSelectProject={selectProject}
        isGuest={isGuest}
      />
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader />
      <main style={{ flex: 1, overflow: 'auto' }}>
        {renderView()}
      </main>
      <SignUpDialog />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <SnackbarProvider maxSnack={5}>
        <ThemeProvider theme={darkTheme}>
          <ReactFlowProvider>
            <AppContent />
            <AuthManager />
          </ReactFlowProvider>
        </ThemeProvider>
      </SnackbarProvider>
    </div>
  );
}

export default App;
